import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { CiCalculationResult } from '../../../_services/ci.service';
import {
  CONSTRUCTION_CLASS_OPTIONS,
  RANGED_BUILDING_BAND,
  needsBuildingRate,
} from '../config/ci-options.config';
import { BuildingValueForm } from '../models/ci-form.model';
import { UiCalculatedBoxComponent } from '../ui/ui-calculated-box.component';
import { UiInputComponent } from '../ui/ui-input.component';
import { UiNoteBarComponent } from '../ui/ui-note-bar.component';
import { UiSelectComponent } from '../ui/ui-select.component';
import { formatCompactCurrency, formatPct, formatPsf, formatSignedPct } from '../../../core/utils/format.util';

/**
 * Independent House: residual building value from the age-band rate table,
 * added to the Final Land Value. Every figure comes from the backend.
 */
@Component({
  selector: 'ci-step-building-value',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    UiInputComponent,
    UiSelectComponent,
    UiCalculatedBoxComponent,
    UiNoteBarComponent,
  ],
  templateUrl: './step-building-value.component.html',
})
export class StepBuildingValueComponent {
  readonly form = input.required<BuildingValueForm>();
  /** Backend result for exactly the current inputs, or null while recalculating. */
  readonly result = input<CiCalculationResult | null>(null);
  readonly calculating = input(false);
  readonly calculationError = input<string | null>(null);

  readonly classOptions = CONSTRUCTION_CLASS_OPTIONS;

  compact = formatCompactCurrency;
  psf = formatPsf;

  land() {
    return this.result()?.landAssessment ?? null;
  }

  /** 13–15 years: the Supply Team picks the rate inside the permitted range. */
  needsRate(): boolean {
    const age = this.form().controls.ageYears.value;
    return age != null && `${age}` !== '' && needsBuildingRate(Number(age));
  }

  rateRange(): { min: number; max: number } | null {
    const cls = this.form().controls.constructionClass.value as 'basic' | 'premium';
    return RANGED_BUILDING_BAND.rates[cls] ?? null;
  }

  rateHint(): string {
    const range = this.rateRange();
    return range
      ? `Between ₹${range.min} and ₹${range.max}/sq.ft for this class.`
      : 'Select the construction classification first.';
  }

  rateOutOfRange(): boolean {
    const range = this.rateRange();
    const raw = this.form().controls.buildingRatePsf.value;
    if (!range || raw == null || `${raw}` === '') return false;
    const rate = Number(raw);
    return rate < range.min || rate > range.max;
  }

  landShare(): string {
    const a = this.land();
    return a ? formatPct(a.landShare * 100, 0) : '—';
  }

  /** Final CI Value with the figures filled in. */
  finalCiFormula(): string {
    const a = this.land();
    if (!a) return 'Final Land Value + Total Building Value';
    return 'Final CI Value = Final Land Value + Total Building Value = '
      + `${this.compact(a.finalLandValue)} + ${this.compact(a.totalBuildingValue)} = ${this.compact(a.assessedValue)}`
      + ` · land ${this.landShare()} of value`;
  }

  askVsCi(): string {
    const a = this.land();
    return a ? formatSignedPct(a.askVsCiPercent * 100, 2) : '—';
  }

  closingRange(): string {
    const r = this.result();
    return r ? `${formatCompactCurrency(r.likelyCloseValueLow)} – ${formatCompactCurrency(r.likelyCloseValueHigh)}` : '—';
  }
}
